import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiResponse, PaginatedResponse, User, Post, Reply, Category, SiteConfig, AdSpot, Ad, File, UserStorage, StoragePrice, StoragePurchase } from '../types';

const API_BASE_URL = '/api';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        
        // 如果是FormData，移除默认的Content-Type，让浏览器自动设置
        if (config.data instanceof FormData) {
          delete config.headers['Content-Type'];
        }
        
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          localStorage.removeItem('auth-storage');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.get(url, config);
    return response.data;
  }

  async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.post(url, data, config);
    return response.data;
  }

  async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.put(url, data, config);
    return response.data;
  }

  async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response: AxiosResponse<T> = await this.client.delete(url, config);
    return response.data;
  }
}

export const api = new ApiClient();

export const userApi = {
  register: (data: { username: string; email: string; password: string }) =>
    api.post<ApiResponse<{ userId: number; username: string; email: string }>>('/users/register', data),

  login: (data: { username: string; password: string }) =>
    api.post<ApiResponse<{ userId: number; username: string; nickname: string; avatar: string | null; role: string; token: string }>>('/users/login', data),

  getCurrentUser: () =>
    api.get<ApiResponse<User>>('/users/me'),

  getUserProfile: (id: number) =>
    api.get<ApiResponse<User>>(`/users/profile/${id}`),

  updateProfile: (data: { nickname?: string; signature?: string; avatar?: string }) =>
    api.put<ApiResponse>('/users/profile', data),

  changePassword: (data: { oldPassword: string; newPassword: string }) =>
    api.put<ApiResponse>('/users/password', data),

  getUserList: (params: { page?: number; pageSize?: number; status?: string; search?: string }) =>
    api.get<PaginatedResponse<User>>('/users/admin/list', { params }),

  searchUser: (query: string) =>
    api.get<ApiResponse>('/users/admin/search', { params: { q: query } }),

  banUser: (id: number) =>
    api.put<ApiResponse>(`/users/admin/${id}/ban`),

  unbanUser: (id: number) =>
    api.put<ApiResponse>(`/users/admin/${id}/unban`),

  deleteUser: (id: number) =>
    api.delete<ApiResponse>(`/users/admin/${id}`),

  updateUserEmail: (id: number, email: string) =>
    api.put<ApiResponse>(`/users/admin/${id}/email`, { email }),

  sendVerificationEmail: (id: number) =>
    api.post<ApiResponse>(`/users/admin/${id}/send-verification`),

  sendResetPasswordEmail: (id: number) =>
    api.post<ApiResponse>(`/users/admin/${id}/send-reset-password`),

  setEmailVerified: (id: number, verified: boolean) =>
    api.put<ApiResponse>(`/users/admin/${id}/email-verified`, { verified }),

  updateUserRole: (id: number, role: string) =>
    api.put<ApiResponse>(`/users/admin/${id}/role`, { role }),

  setUserGroup: (id: number, userGroupId: number | null) =>
    api.put<ApiResponse>(`/users/admin/${id}/user-group`, { userGroupId }),

  // 用户组相关 API
  getUserGroups: () =>
    api.get<ApiResponse>(`/user-groups/admin/list`),
  getUserGroup: (id: number) =>
    api.get<ApiResponse>(`/user-groups/admin/${id}`),
  createUserGroup: (groupData: { name: string; description?: string; role?: string; permissions?: any; sort_order?: number }) =>
    api.post<ApiResponse>('/user-groups/admin', groupData),
  updateUserGroup: (id: number, groupData: { name?: string; description?: string; role?: string; permissions?: any; sort_order?: number }) =>
    api.put<ApiResponse>(`/user-groups/admin/${id}`, groupData),
  deleteUserGroup: (id: number) =>
    api.delete<ApiResponse>(`/user-groups/admin/${id}`),

  forgotPassword: (email: string) =>
    api.post<ApiResponse>('/users/forgot-password', { email }),

  resetPassword: (token: string, password: string) =>
    api.post<ApiResponse>('/users/reset-password', { token, password }),
};



export const categoryApi = {
  getAllCategories: () =>
    api.get<ApiResponse<Category[]>>('/categories'),

  getCategories: () =>
    api.get<ApiResponse<Category[]>>('/categories'),

  getAllCategoriesForAdmin: () =>
    api.get<ApiResponse<Category[]>>('/categories/all'),

  getCategoryById: (id: number) =>
    api.get<ApiResponse<Category>>(`/categories/${id}`),

  createCategory: (data: { name: string; description?: string; icon?: string; sort_order?: number; parent_id?: number }) =>
    api.post<ApiResponse<{ categoryId: number }>>('/categories', data),

  updateCategory: (id: number, data: { name?: string; description?: string; icon?: string; sort_order?: number; status?: string }) =>
    api.put<ApiResponse>(`/categories/${id}`, data),

  deleteCategory: (id: number) =>
    api.delete<ApiResponse>(`/categories/${id}`),
};

export const uploadApi = {
  uploadImage: (file: File) => {
    const formData = new FormData();
    formData.append('image', file);
    // 不要手动设置Content-Type，浏览器会自动设置正确的multipart/form-data类型
    return api.post<ApiResponse<{ url: string; filename: string; size: number }>>('/upload/image', formData);
  },
  
  uploadAttachment: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    // 不要手动设置Content-Type，浏览器会自动设置正确的multipart/form-data类型
    return api.post<ApiResponse<{ id: number; url: string; filename: string; stored_name: string; size: number; type: string; ext: string; is_image: boolean }>>('/upload/attachment', formData);
  },
};

export const storageApi = {
  getStorageInfo: () =>
    api.get<ApiResponse<UserStorage>>('/storage/info'),
    
  getFileList: (params?: { page?: number; pageSize?: number }) =>
    api.get<ApiResponse<{ files: File[]; total: number; page: number; pageSize: number }>>('/storage/files', { params }),
    
  deleteFile: (id: number) =>
    api.delete<ApiResponse>(`/storage/files/${id}`),
    
  downloadFile: (id: number) =>
    `${API_BASE_URL}/storage/download/${id}`,
    
  getPriceList: () =>
    api.get<ApiResponse<StoragePrice[]>>('/storage/prices'),
    
  purchaseSpace: (data: { priceId: number; paymentMethod?: string }) =>
    api.post<ApiResponse<{ 
      purchase_id: number; 
      order_no: string;
      space_added: number; 
      space_added_formatted: string;
      price: number;
      payment_address: string;
      contract_address: string;
    }>>('/storage/purchase', data),
    
  getPurchaseHistory: (params?: { page?: number; pageSize?: number }) =>
    api.get<ApiResponse<{ purchases: StoragePurchase[]; total: number; page: number; pageSize: number }>>('/storage/purchases', { params }),
  
  confirmPayment: (purchaseId: number) =>
    api.post<ApiResponse<{ space_added: number; space_added_formatted: string }>>('/storage/confirm-payment', { purchaseId }),
};

// 更新发帖 API，支持附件
export const postApi = {
  getPosts: (params: { page?: number; pageSize?: number; categoryId?: number; orderBy?: string }) =>
    api.get<PaginatedResponse<Post>>('/posts', { params }),

  searchPosts: (params: { keyword: string; page?: number; pageSize?: number }) =>
    api.get<PaginatedResponse<Post>>('/posts/search', { params }),

  getHotPosts: (limit?: number) =>
    api.get<ApiResponse<Post[]>>('/posts/hot', { params: { limit } }),

  getPostById: (id: number, params?: { page?: number; pageSize?: number }) =>
    api.get<ApiResponse<{ post: Post; replies: Reply[]; replyTotal: number; attachments: File[] }>>(`/posts/${id}`, { params }),

  createPost: (data: { title: string; content: string; category_id: number; attachment_ids?: number[] }) =>
    api.post<ApiResponse<{ postId: number }>>('/posts', data),

  updatePost: (id: number, data: { title?: string; content?: string; category_id?: number; is_top?: 'none' | 'category' | 'global'; is_essence?: boolean; status?: string }) =>
    api.put<ApiResponse>(`/posts/${id}`, data),

  deletePost: (id: number) =>
    api.delete<ApiResponse>(`/posts/${id}`),

  createReply: (postId: number, data: { content: string; parent_id?: number }) =>
    api.post<ApiResponse<{ replyId: number }>>(`/posts/${postId}/replies`, data),

  deleteReply: (id: number) =>
    api.delete<ApiResponse>(`/posts/replies/${id}`),

  likePost: (id: number) =>
    api.post<ApiResponse<{ liked: boolean }>>(`/posts/${id}/like`),

  likeReply: (id: number) =>
    api.post<ApiResponse<{ liked: boolean }>>(`/posts/replies/${id}/like`),

  getUserPosts: (userId: number, params: { page?: number; pageSize?: number }) =>
    api.get<PaginatedResponse<Post>>(`/posts/user/${userId}`, { params }),

  getUserReplies: (userId: number, params: { page?: number; pageSize?: number }) =>
    api.get<PaginatedResponse<any>>(`/posts/user/${userId}/replies`, { params }),

  // 管理员获取所有帖子（包括板块置顶）
  getAdminPosts: (params?: { page?: number; pageSize?: number; keyword?: string }) =>
    api.get<PaginatedResponse<Post>>(`/posts/admin/list`, { params }),
};

export const siteConfigApi = {
  getPublicConfigs: () =>
    api.get<ApiResponse<Record<string, string>>>('/site-config/public'),

  getAllConfigs: () =>
    api.get<ApiResponse<Record<string, SiteConfig[]>>>('/site-config'),

  getConfig: (key: string) =>
    api.get<ApiResponse<SiteConfig>>(`/site-config/${key}`),

  updateConfig: (key: string, value: string) =>
    api.put<ApiResponse>(`/site-config/${key}`, { value }),

  batchUpdateConfigs: (configs: Array<{ key: string; value: string }>) =>
    api.post<ApiResponse>('/site-config/batch', { configs }),

  addConfig: (data: { config_key: string; config_value: string; config_type?: string; description?: string; group_name?: string; is_editable?: boolean }) =>
    api.post<ApiResponse>('/site-config', data),

  deleteConfig: (key: string) =>
    api.delete<ApiResponse>(`/site-config/${key}`),
};

export const adApi = {
  getActiveAds: () =>
    api.get<ApiResponse<Record<string, Ad[]>>>('/ads/active'),

  clickAd: (id: number) =>
    api.post<ApiResponse>(`/ads/${id}/click`),

  getAllAdSpots: () =>
    api.get<ApiResponse<AdSpot[]>>('/ads/spots'),

  getAdSpot: (id: number) =>
    api.get<ApiResponse<AdSpot>>(`/ads/spots/${id}`),

  addAdSpot: (data: { spot_name: string; spot_key: string; position: string; description?: string; is_active?: boolean; sort_order?: number }) =>
    api.post<ApiResponse>('/ads/spots', data),

  updateAdSpot: (id: number, data: { spot_name?: string; spot_key?: string; position?: string; description?: string; is_active?: boolean; sort_order?: number }) =>
    api.put<ApiResponse>(`/ads/spots/${id}`, data),

  deleteAdSpot: (id: number) =>
    api.delete<ApiResponse>(`/ads/spots/${id}`),

  getAdsBySpot: (spotId: number, params?: { page?: number; pageSize?: number }) =>
    api.get<PaginatedResponse<Ad>>(`/ads/spots/${spotId}/ads`, { params }),

  getAd: (id: number) =>
    api.get<ApiResponse<Ad>>(`/ads/ads/${id}`),

  addAd: (data: { spot_id: number; title?: string; content?: string; image_url?: string; link_url?: string; start_time?: string; end_time?: string; is_active?: boolean; sort_order?: number }) =>
    api.post<ApiResponse>('/ads/ads', data),

  updateAd: (id: number, data: { spot_id?: number; title?: string; content?: string; image_url?: string; link_url?: string; start_time?: string; end_time?: string; is_active?: boolean; sort_order?: number }) =>
    api.put<ApiResponse>(`/ads/ads/${id}`, data),

  deleteAd: (id: number) =>
    api.delete<ApiResponse>(`/ads/ads/${id}`),
};

export const sensitiveWordApi = {
  getAllWords: () =>
    api.get<ApiResponse<any[]>>('/sensitive-words'),

  addWord: (word: string) =>
    api.post<ApiResponse>('/sensitive-words', { word }),

  deleteWord: (id: number) =>
    api.delete<ApiResponse>(`/sensitive-words/${id}`),

  checkContent: (content: string) =>
    api.post<ApiResponse<{ hasSensitive: boolean; filteredContent: string }>>('/sensitive-words/check', { content }),
};

export const statsApi = {
  getDashboardStats: () =>
    api.get<ApiResponse<{ users: number; posts: number; replies: number; categories: number }>>('/stats/dashboard'),

  getTimeSeriesStats: (period: '5m' | '1h' | '24h' | '7d') =>
    api.get<ApiResponse<Array<{ time: string; users: number; posts: number; replies: number }>>>('/stats/time-series', { params: { period } }),
};

export const reconciliationApi = {
  getErc20Data: (address?: string) =>
    api.get<ApiResponse<{ balance: string; transactions: any[] }>>('/reconciliation/erc20-data', { params: { address } }),
};

export const emailApi = {
  sendEmail: (data: { to: string; subject: string; content: string }) =>
    api.post<ApiResponse>('/email/send', data),
};

export interface UserLevelInfo {
  level: number;
  levelName: string;
  levelIcon: string;
  points: number;
  growthPoints: number;
  progress: number;
  nextLevel: { name: string; icon: string; pointsNeeded: number } | null;
  titles: Array<{ name: string; icon: string; color: string }>;
  achievements: Array<{ name: string; icon: string; description: string; unlockedAt: Date | null }>;
}

export interface PointsHistoryRecord {
  id: number;
  user_id: number;
  action: string;
  action_name: string;
  points: number;
  growth_points: number;
  balance_points: number;
  balance_growth: number;
  target_type: string | null;
  target_id: number | null;
  description: string;
  created_at: string;
}

export interface PointsHistoryResponse {
  records: PointsHistoryRecord[];
  total: number;
  page: number;
  pageSize: number;
}

export const pointsApi = {
  getUserLevelInfo: (userId: number) =>
    api.get<ApiResponse<UserLevelInfo>>(`/points/user/${userId}/info`),

  getPointsHistory: (userId: number, params?: { page?: number; pageSize?: number }) =>
    api.get<ApiResponse<PointsHistoryResponse>>(`/points/user/${userId}/history`, { params }),

  getAllLevels: () =>
    api.get<ApiResponse<any[]>>('/points/levels'),

  getAllRules: () =>
    api.get<ApiResponse<any[]>>('/points/rules'),
};

export interface OperationLog {
  id: number;
  user_id: number;
  username: string;
  user_role: string;
  action: string;
  target_type: string;
  target_id: number | null;
  target_name: string | null;
  details: Record<string, any>;
  ip_address: string;
  user_agent: string;
  created_at: string;
}

export const operationLogApi = {
  // 获取操作日志列表
  getLogs: (params?: {
    action?: string;
    targetType?: string;
    operatorUserId?: number;
    startDate?: string;
    endDate?: string;
    page?: number;
    pageSize?: number;
  }) =>
    api.get<PaginatedResponse<OperationLog>>('/operation-logs', { params }),

  // 导出操作日志（仅站长可用）
  exportLogs: (params?: {
    action?: string;
    targetType?: string;
    startDate?: string;
    endDate?: string;
  }) =>
    api.get<Blob>('/operation-logs/export', {
      params,
      responseType: 'blob',
    }),

  // 获取操作类型统计
  getActionStats: () =>
    api.get<ApiResponse<Array<{ action: string; count: number }>>>('/operation-logs/stats/actions'),

  // 获取用户操作统计（仅站长可用）
  getUserStats: () =>
    api.get<ApiResponse<Array<{ user_id: number; username: string; count: number }>>>('/operation-logs/stats/users'),
};

export interface Message {
  id: number;
  user_id: number;
  from_user_id: number | null;
  from_username: string | null;
  type: 'system' | 'admin' | 'like' | 'reply' | 'post_action';
  title: string;
  content: string;
  related_id: number | null;
  related_type: 'post' | 'reply' | 'comment' | null;
  is_read: number;
  created_at: string;
  read_at: string | null;
}

export const messageApi = {
  getMessages: (params: { page?: number; pageSize?: number; type?: string }) =>
    api.get<ApiResponse<{ messages: Message[]; pagination: PaginatedResponse }>>('/messages', { params }),

  getUnreadCount: () =>
    api.get<ApiResponse<{ count: number }>>('/messages/unread-count'),

  markAsRead: (messageId: number) =>
    api.put<ApiResponse>(`/messages/${messageId}/read`),

  markAllAsRead: () =>
    api.put<ApiResponse>('/messages/read-all'),

  deleteMessage: (messageId: number) =>
    api.delete<ApiResponse>(`/messages/${messageId}`),
};